import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Plus, Edit, Save, Settings, BarChart, Database, CheckCircle, XCircle, Upload, Image, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useOpsDashboard } from '@/context/OpsDashboardContext';

interface Show {
  id: string;
  show_name: string;
  creator: string;
  vertical: string;
  artie_score: number;
  production_status: string;
  is_marketplace_eligible?: boolean;
  logline?: string;
  hero_image_url?: string;
  reason_to_believe?: string;
  heuristic_tags?: string[];
  assets?: ShowAsset[];
}

interface ShowAsset {
  id: string;
  filename: string;
  s3_url: string;
  asset_type: string;
  description?: string;
  content_type: string;
  file_size: number;
  uploaded_at: string;
}

interface PlatformMetric {
  id?: string;
  _id?: string;
  show_id: string;
  platform: 'tiktok' | 'instagram' | 'imdb' | 'youtube';
  metric_type: string;
  value: number;
  notes?: string;
  created_at?: string;
}

interface AdminSummary {
  shows: {
    total: number;
    by_status: Record<string, number>;
  };
  slots: {
    total: number;
    by_status: Record<string, number>;
  };
  brands: {
    total: number;
    by_status: Record<string, number>;
  };
}

const AdminPanelPage: React.FC = () => {
  const { refreshTriggers } = useOpsDashboard();
  const [shows, setShows] = useState<Show[]>([]);
  const [summary, setSummary] = useState<AdminSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedShow, setSelectedShow] = useState<Show | null>(null);
  const [isMetricDialogOpen, setIsMetricDialogOpen] = useState(false);
  const [isShowDialogOpen, setIsShowDialogOpen] = useState(false);
  const [isAssetDialogOpen, setIsAssetDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadingAsset, setUploadingAsset] = useState(false);
  const [showAssets, setShowAssets] = useState<ShowAsset[]>([]);
  const [platformMetrics, setPlatformMetrics] = useState<PlatformMetric[]>([]);
  const [metricForm, setMetricForm] = useState<PlatformMetric>({
    show_id: '',
    platform: 'tiktok',
    metric_type: '',
    value: 0,
    notes: ''
  });
  const [showForm, setShowForm] = useState({
    logline: '',
    hero_image_url: '',
    reason_to_believe: '',
    heuristic_tags: [] as string[],
    is_eligible: false,
    approval_notes: ''
  });
  const [assetForm, setAssetForm] = useState({
    asset_type: 'hero_image',
    description: ''
  });
  const [metricFormErrors, setMetricFormErrors] = useState<{
    show_id?: string;
    metric_type?: string;
    value?: string;
  }>({});
  const { toast } = useToast();

  const platforms = [
    { value: 'tiktok', label: 'TikTok' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'imdb', label: 'IMDB' },
    { value: 'youtube', label: 'YouTube' }
  ];

  const metricTypes = [
    'followers', 'views', 'likes', 'comments', 'shares', 'engagement_rate',
    'avg_views_per_video', 'subscriber_growth', 'rating', 'reviews_count'
  ];

  const heuristicTagOptions = [
    'High Production Value', 'Celebrity Cast', 'Viral Potential', 'Brand Safe',
    'Youth Appeal', 'Global Appeal', 'Trending Topic', 'Award Potential',
    'Social Media Friendly', 'Advertiser Friendly'
  ];

  const fetchShows = async () => {
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/v1/ops-admin/shows`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch shows');
      }

      const result = await response.json();
      setShows(result);
    } catch (error) {
      console.error('Error fetching shows:', error);
      toast({
        title: "Error",
        description: "Failed to fetch shows",
        variant: "destructive"
      });
    }
  };

  const fetchSummary = async () => {
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/v1/ops-admin/analytics/summary`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch summary');
      }

      const result = await response.json();
      setSummary(result);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const handleAddMetric = async () => {
    const errors: { show_id?: string; metric_type?: string; value?: string } = {};
    if (!metricForm.show_id) errors.show_id = 'Show is required.';
    if (!metricForm.metric_type) errors.metric_type = 'Metric type is required.';
    if (!Number.isFinite(metricForm.value) || metricForm.value < 0) {
      errors.value = 'Value must be a non-negative number.';
    }
    if (Object.keys(errors).length > 0) {
      setMetricFormErrors(errors);
      toast({
        title: "Validation Error",
        description: "Please correct the metric form fields.",
        variant: "destructive"
      });
      return;
    }

    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/v1/ops-admin/platform-metrics`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(metricForm)
      });

      if (!response.ok) {
        throw new Error('Failed to add metric');
      }

      toast({
        title: "Success",
        description: "Platform metric added successfully"
      });

      // Refresh metrics for the current show
      if (metricForm.show_id) {
        await fetchPlatformMetrics(metricForm.show_id);
      }

      setIsMetricDialogOpen(false);
      setMetricForm({
        show_id: '',
        platform: 'tiktok',
        metric_type: '',
        value: 0,
        notes: ''
      });
      setMetricFormErrors({});
    } catch (error) {
      console.error('Error adding metric:', error);
      toast({
        title: "Error",
        description: "Failed to add platform metric",
        variant: "destructive"
      });
    }
  };

  const handleUpdateShowMetadata = async () => {
    if (!selectedShow) return;

    if (showForm.hero_image_url.trim()) {
      try {
        new URL(showForm.hero_image_url);
      } catch {
        toast({
          title: "Validation Error",
          description: "Hero image URL must be a valid URL.",
          variant: "destructive"
        });
        return;
      }
    }

    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/v1/ops-admin/shows/${selectedShow.id}/metadata`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          show_id: selectedShow.id,
          logline: showForm.logline,
          hero_image_url: showForm.hero_image_url,
          reason_to_believe: showForm.reason_to_believe,
          heuristic_tags: showForm.heuristic_tags
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update show metadata');
      }

      toast({
        title: "Success",
        description: "Show metadata updated successfully"
      });

      setIsShowDialogOpen(false);
      fetchShows();
    } catch (error) {
      console.error('Error updating show metadata:', error);
      toast({
        title: "Error",
        description: "Failed to update show metadata",
        variant: "destructive"
      });
    }
  };

  const handleToggleEligibility = async (showId: string, isEligible: boolean) => {
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/v1/ops-admin/shows/${showId}/eligibility`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          show_id: showId,
          is_eligible: isEligible,
          approval_notes: showForm.approval_notes
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update eligibility');
      }

      toast({
        title: "Success",
        description: "Marketplace eligibility updated successfully"
      });

      fetchShows();
    } catch (error) {
      console.error('Error updating eligibility:', error);
      toast({
        title: "Error",
        description: "Failed to update marketplace eligibility",
        variant: "destructive"
      });
    }
  };


  const openShowDialog = (show: Show) => {
    setSelectedShow(show);
    setShowForm({
      logline: show.logline || '',
      hero_image_url: show.hero_image_url || '',
      reason_to_believe: show.reason_to_believe || '',
      heuristic_tags: show.heuristic_tags || [],
      is_eligible: show.is_marketplace_eligible || false,
      approval_notes: ''
    });
    setIsShowDialogOpen(true);
  };

  const handleTagToggle = (tag: string, checked: boolean) => {
    setShowForm(prev => ({
      ...prev,
      heuristic_tags: checked
        ? [...prev.heuristic_tags, tag]
        : prev.heuristic_tags.filter(t => t !== tag)
    }));
  };

  const handleFileUpload = async () => {
    if (!selectedShow) return;
    if (!selectedFile) {
      toast({
        title: "Validation Error",
        description: "Please select an image file to upload.",
        variant: "destructive"
      });
      return;
    }
    if (!selectedFile.type.startsWith('image/')) {
      toast({
        title: "Validation Error",
        description: "Only image files are allowed.",
        variant: "destructive"
      });
      return;
    }
    const maxFileSizeBytes = 10 * 1024 * 1024;
    if (selectedFile.size > maxFileSizeBytes) {
      toast({
        title: "Validation Error",
        description: "Image size must be 10MB or less.",
        variant: "destructive"
      });
      return;
    }

    try {
      setUploadingAsset(true);
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      let endpoint = '';
      if (assetForm.asset_type === 'hero_image') {
        endpoint = `${apiBaseUrl}/api/v1/ops-admin/shows/${selectedShow.id}/upload-hero-image`;
      } else {
        endpoint = `${apiBaseUrl}/api/v1/ops-admin/shows/${selectedShow.id}/upload-asset`;
        formData.append('asset_type', assetForm.asset_type);
        if (assetForm.description) {
          formData.append('description', assetForm.description);
        }
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to upload asset');
      }

      const result = await response.json();
      
      toast({
        title: "Success",
        description: result.message
      });

      setIsAssetDialogOpen(false);
      setSelectedFile(null);
      setAssetForm({ asset_type: 'hero_image', description: '' });
      
      // Refresh show data
      fetchShows();
      if (assetForm.asset_type !== 'hero_image') {
        fetchShowAssets(selectedShow.id);
      }
    } catch (error) {
      console.error('Error uploading asset:', error);
      toast({
        title: "Error",
        description: "Failed to upload asset",
        variant: "destructive"
      });
    } finally {
      setUploadingAsset(false);
    }
  };

  const fetchShowAssets = async (showId: string) => {
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/v1/ops-admin/shows/${showId}/assets`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setShowAssets(data.assets || []);
      }
    } catch (error) {
      console.error('Error fetching assets:', error);
    }
  };

  const fetchPlatformMetrics = async (showId: string) => {
    try {
      console.log(`DEBUG: Fetching platform metrics for show: ${showId}`);
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/v1/ops-admin/platform-metrics/${showId}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        console.log('DEBUG: Platform metrics response:', data);
        
        // Handle both old format (direct array) and new format (object with metrics property)
        const metrics = data.metrics || data;
        setPlatformMetrics(metrics || []);
      } else {
        console.error(`Error fetching platform metrics: ${response.status} ${response.statusText}`);
        setPlatformMetrics([]);
      }
    } catch (error) {
      console.error('Error fetching platform metrics:', error);
      setPlatformMetrics([]);
    }
  };

  const handleDeletePlatformMetric = async (metricId: string) => {
    try {
      console.log(`DEBUG: Deleting platform metric: ${metricId}`);
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/v1/ops-admin/platform-metrics/${metricId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Platform metric deleted successfully"
        });
        
        // Refresh metrics for the current show
        if (metricForm.show_id) {
          await fetchPlatformMetrics(metricForm.show_id);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error deleting platform metric:', error);
      toast({
        title: "Error",
        description: "Failed to delete platform metric",
        variant: "destructive"
      });
    }
  };

  const handleDeleteAsset = async (showId: string, assetId: string) => {
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/v1/ops-admin/shows/${showId}/assets/${assetId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Asset deleted successfully"
        });
        fetchShowAssets(showId);
      }
    } catch (error) {
      console.error('Error deleting asset:', error);
      toast({
        title: "Error",
        description: "Failed to delete asset",
        variant: "destructive"
      });
    }
  };

  const openAssetDialog = (show: Show) => {
    setSelectedShow(show);
    setIsAssetDialogOpen(true);
    fetchShowAssets(show.id);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchShows(), fetchSummary()]);
      setLoading(false);
    };
    loadData();
  }, []);

  // Listen for refresh triggers from the context
  useEffect(() => {
    if (refreshTriggers.admin > 0) {
      const loadData = async () => {
        await Promise.all([fetchShows(), fetchSummary()]);
      };
      loadData();
    }
  }, [refreshTriggers.admin]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
          <p className="text-gray-600">Manual data entry and show management</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={isMetricDialogOpen} onOpenChange={setIsMetricDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Platform Metric
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Platform Metric</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="show">Show</Label>
                  <Select value={metricForm.show_id} onValueChange={(value) => setMetricForm(prev => ({ ...prev, show_id: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select show" />
                    </SelectTrigger>
                    <SelectContent>
                      {shows.map(show => (
                        <SelectItem key={show.id} value={show.id}>
                          {show.show_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {metricFormErrors.show_id && <p className="text-xs text-red-600 mt-1">{metricFormErrors.show_id}</p>}
                </div>
                <div>
                  <Label htmlFor="platform">Platform</Label>
                  <Select value={metricForm.platform} onValueChange={(value: any) => setMetricForm(prev => ({ ...prev, platform: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {platforms.map(platform => (
                        <SelectItem key={platform.value} value={platform.value}>
                          {platform.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="metric_type">Metric Type</Label>
                  <Select value={metricForm.metric_type} onValueChange={(value) => setMetricForm(prev => ({ ...prev, metric_type: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select metric type" />
                    </SelectTrigger>
                    <SelectContent>
                      {metricTypes.map(type => (
                        <SelectItem key={type} value={type}>
                          {type.replace('_', ' ')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {metricFormErrors.metric_type && <p className="text-xs text-red-600 mt-1">{metricFormErrors.metric_type}</p>}
                </div>
                <div>
                  <Label htmlFor="value">Value</Label>
                  <Input
                    id="value"
                    type="number"
                    value={metricForm.value}
                    onChange={(e) => setMetricForm(prev => ({ ...prev, value: Number(e.target.value) }))}
                  />
                  {metricFormErrors.value && <p className="text-xs text-red-600 mt-1">{metricFormErrors.value}</p>}
                </div>
                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={metricForm.notes}
                    onChange={(e) => setMetricForm(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsMetricDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleAddMetric}>
                    Add Metric
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <Database className="h-4 w-4" />
                Shows
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary.shows.total}</div>
              <div className="text-xs text-gray-500 space-y-1">
                {Object.entries(summary.shows.by_status).map(([status, count]) => (
                  <div key={status} className="flex justify-between">
                    <span>{status.replace('_', ' ')}</span>
                    <span>{count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <BarChart className="h-4 w-4" />
                Slots
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary.slots.total}</div>
              <div className="text-xs text-gray-500 space-y-1">
                {Object.entries(summary.slots.by_status).map(([status, count]) => (
                  <div key={status} className="flex justify-between">
                    <span>{status}</span>
                    <span>{count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Brands
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary.brands.total}</div>
              <div className="text-xs text-gray-500 space-y-1">
                {Object.entries(summary.brands.by_status).map(([status, count]) => (
                  <div key={status} className="flex justify-between">
                    <span>{status.replace('_', ' ')}</span>
                    <span>{count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content */}
      <Tabs defaultValue="shows" className="space-y-4">
        <TabsList>
          <TabsTrigger value="shows">Show Management</TabsTrigger>
          <TabsTrigger value="metrics">Platform Metrics</TabsTrigger>
        </TabsList>

        <TabsContent value="shows">
          <Card>
            <CardHeader>
              <CardTitle>Show Management</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Show Name</TableHead>
                      <TableHead>Creator</TableHead>
                      <TableHead>Vertical</TableHead>
                      <TableHead>Artie Score</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {shows.map((show) => (
                      <TableRow key={show.id}>
                        <TableCell className="font-medium">{show.show_name}</TableCell>
                        <TableCell>{show.creator}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{show.vertical}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={show.artie_score >= 80 ? 'default' : 'secondary'}>
                            {show.artie_score.toFixed(1)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openShowDialog(show)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="metrics">
          <Card>
            <CardHeader>
              <CardTitle>Platform Metrics</CardTitle>
              <p className="text-sm text-gray-600">View and manage platform metrics for shows</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Show Selection */}
                <div>
                  <Label htmlFor="metrics-show">Select Show</Label>
                  <Select
                    value={metricForm.show_id}
                    onValueChange={async (value) => {
                      setMetricForm(prev => ({ ...prev, show_id: value }));
                      if (value) {
                        await fetchPlatformMetrics(value);
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a show to view metrics" />
                    </SelectTrigger>
                    <SelectContent>
                      {shows.map(show => (
                        <SelectItem key={show.id} value={show.id}>
                          {show.show_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Platform Metrics Display */}
                {metricForm.show_id && (
                  <div className="mt-6">
                    <h3 className="text-lg font-medium mb-4">
                      Metrics for {shows.find(s => s.id === metricForm.show_id)?.show_name}
                    </h3>
                    
                    {platformMetrics.length > 0 ? (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Platform</TableHead>
                              <TableHead>Metric Type</TableHead>
                              <TableHead>Value</TableHead>
                              <TableHead>Date Added</TableHead>
                              <TableHead>Notes</TableHead>
                              <TableHead>Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {platformMetrics.map((metric, index) => (
                              <TableRow key={metric._id || index}>
                                <TableCell>
                                  <Badge variant="outline">
                                    {metric.platform}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  {metric.metric_type.replace('_', ' ')}
                                </TableCell>
                                <TableCell className="font-medium">
                                  {metric.value.toLocaleString()}
                                </TableCell>
                                <TableCell>
                                  {new Date(metric.created_at || '').toLocaleDateString()}
                                </TableCell>
                                <TableCell>
                                  <div className="max-w-xs truncate" title={metric.notes}>
                                    {metric.notes || '-'}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeletePlatformMetric(metric._id)}
                                    className="text-red-600 hover:text-red-800 hover:bg-red-50"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No platform metrics found for this show.</p>
                        <p className="text-sm">Use the "Add Platform Metric" button above to add metrics.</p>
                      </div>
                    )}
                  </div>
                )}

                {!metricForm.show_id && (
                  <div className="text-center py-12 text-gray-500">
                    <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Select a show to view its platform metrics.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>

      {/* Show Edit Dialog */}
      <Dialog open={isShowDialogOpen} onOpenChange={setIsShowDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Show Metadata</DialogTitle>
          </DialogHeader>
          {selectedShow && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="logline">Logline</Label>
                <Textarea
                  id="logline"
                  value={showForm.logline}
                  onChange={(e) => setShowForm(prev => ({ ...prev, logline: e.target.value }))}
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor="hero_image_url">Hero Image URL</Label>
                <Input
                  id="hero_image_url"
                  value={showForm.hero_image_url}
                  onChange={(e) => setShowForm(prev => ({ ...prev, hero_image_url: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="reason_to_believe">Reason to Believe</Label>
                <Textarea
                  id="reason_to_believe"
                  value={showForm.reason_to_believe}
                  onChange={(e) => setShowForm(prev => ({ ...prev, reason_to_believe: e.target.value }))}
                  rows={3}
                />
              </div>
              <div>
                <Label>Heuristic Tags</Label>
                <div className="grid grid-cols-2 gap-2 mt-2 max-h-32 overflow-y-auto">
                  {heuristicTagOptions.map(tag => (
                    <label key={tag} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={showForm.heuristic_tags.includes(tag)}
                        onChange={(e) => handleTagToggle(tag, e.target.checked)}
                      />
                      <span className="text-sm">{tag}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setIsShowDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleUpdateShowMetadata}>
                  Update Metadata
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Asset Upload Dialog */}
      <Dialog open={isAssetDialogOpen} onOpenChange={setIsAssetDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Show Assets - {selectedShow?.show_name}</DialogTitle>
          </DialogHeader>
          {selectedShow && (
            <div className="space-y-6">
              {/* Upload Section */}
              <div className="border rounded-lg p-4 space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  Upload New Asset
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="asset_type">Asset Type</Label>
                    <Select
                      value={assetForm.asset_type}
                      onValueChange={(value) => setAssetForm(prev => ({ ...prev, asset_type: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hero_image">Hero Image</SelectItem>
                        <SelectItem value="poster">Poster</SelectItem>
                        <SelectItem value="still">Still Image</SelectItem>
                        <SelectItem value="behind_scenes">Behind the Scenes</SelectItem>
                        <SelectItem value="promotional">Promotional Material</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="description">Description (Optional)</Label>
                    <Input
                      id="description"
                      value={assetForm.description}
                      onChange={(e) => setAssetForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Brief description of the asset"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="file">Select File</Label>
                  <Input
                    id="file"
                    type="file"
                    accept="image/*"
                    onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                    className="mt-1"
                  />
                  {selectedFile && (
                    <div className="mt-2 text-sm text-gray-600">
                      Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSelectedFile(null);
                      setAssetForm({ asset_type: 'hero_image', description: '' });
                    }}
                  >
                    Clear
                  </Button>
                  <Button
                    onClick={handleFileUpload}
                    disabled={!selectedFile || uploadingAsset}
                  >
                    {uploadingAsset ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Asset
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Current Assets Section */}
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <Image className="h-4 w-4" />
                  Current Assets
                </h3>

                {/* Hero Image */}
                {selectedShow.hero_image_url && (
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium">Hero Image</h4>
                      <Badge variant="default">Primary</Badge>
                    </div>
                    <div className="flex items-center gap-4">
                      <img
                        src={selectedShow.hero_image_url}
                        alt="Hero"
                        className="w-20 h-20 object-cover rounded border"
                        onError={(e) => {
                          e.currentTarget.src = '/placeholder.svg';
                        }}
                      />
                      <div className="flex-1">
                        <p className="text-sm text-gray-600">Current hero image for the show</p>
                        <a
                          href={selectedShow.hero_image_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline text-sm"
                        >
                          View full size
                        </a>
                      </div>
                    </div>
                  </div>
                )}

                {/* Other Assets */}
                {showAssets.length > 0 ? (
                  <div className="space-y-3">
                    {showAssets.map((asset) => (
                      <div key={asset.id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <img
                              src={asset.s3_url}
                              alt={asset.filename}
                              className="w-16 h-16 object-cover rounded border"
                              onError={(e) => {
                                e.currentTarget.src = '/placeholder.svg';
                              }}
                            />
                            <div>
                              <h4 className="font-medium">{asset.filename}</h4>
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Badge variant="outline">{asset.asset_type.replace('_', ' ')}</Badge>
                                <span>{formatFileSize(asset.file_size)}</span>
                                <span>•</span>
                                <span>{new Date(asset.uploaded_at).toLocaleDateString()}</span>
                              </div>
                              {asset.description && (
                                <p className="text-sm text-gray-600 mt-1">{asset.description}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <a
                              href={asset.s3_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline text-sm"
                            >
                              View
                            </a>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteAsset(selectedShow.id, asset.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Image className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No additional assets uploaded yet</p>
                  </div>
                )}
              </div>

              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setIsAssetDialogOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanelPage;